import React, { useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import makeStyles from '@mui/styles/makeStyles';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import ScoreIcon from '@ts-core/components/ScoreIcon';
import TSTable from '@ts-core/components/Table';
import { DEFAULT_EXERCISE_TYPE } from '@ts-core/utils/Constants';

import QuestionStatsPopover from 'modules/subjects/components/QuestionStatsPopover/QuestionStatsPopover';

import { formatValue } from '@ts-core/utils/dataTableUtils';

import AssignmentResultTimeCell from './AssignmentResultTimeCell';
import AssignmentResultQuestionHeadCell from './AssignmentResultQuestionHeadCell';

const useStyles = makeStyles((theme) => ({
  resultCell: {
    paddingLeft: 12,
    paddingRight: theme.spacing(1),
  },
  nameCell: {
    minWidth: 160,
  },
}));

const AssignmentResultsTable = (props) => {
  const intl = useIntl();
  const classes = useStyles();

  const {
    courseId,
    results,
    students,
    loading,
    onlyListStudentsWithoutAnswers,
    assignment,
    onFilterResults,
    onToggleOnlyListStudentsWithoutAnswers,
    onGetXlsxData,
  } = props;
  const { exercises = [] } = results;

  const [questionPopoverData, setQuestionPopoverData] = useState({});

  const handleOpenQuestionPopover = useCallback((event, question) => {
    const { answerTypes, answers } = question;
    const title = `${question.exercise} - ${question.name}`;
    setQuestionPopoverData({
      questionData: {
        title,
        answersCount: answers,
        answerTypes,
      },
      anchorEl: event.target,
    });
  }, []);

  const handleCloseQuestionPopover = useCallback(() => {
    setQuestionPopoverData({});
  }, []);

  const renderScore = useCallback(
    (value, column) => {
      if (column.id === 'score') {
        return formatValue(value, results.maxScore);
      }
      return <ScoreIcon score={value} />;
    },
    [results]
  );

  const columns = useMemo(() => {
    const result = [
      {
        Header: intl.formatMessage({ id: 'assignmentResultsTable.student' }),
        value: intl.formatMessage({ id: 'assignmentResultsTable.student' }),
        className: classes.nameCell,
        Footer: intl.formatMessage({ id: 'averages' }),
        accessor: 'name',
        sortable: true,
      },
    ];

    if (students.some((s) => s.group)) {
      result.push({
        Header: intl.formatMessage({ id: 'assignmentResultsTable.group' }),
        value: intl.formatMessage({ id: 'assignmentResultsTable.group' }),
        accessor: 'group',
        id: 'group',
        sortable: true,
        filterable: true,
        filterMethod: (filter, row) => {
          if (!filter || filter.value === 'all') return true;
          return row.group && row.group === filter.value;
        },
      });
    }

    result.push({
      Header: intl.formatMessage({ id: 'result' }),
      value: intl.formatMessage({ id: 'result' }),
      accessor: 'score',
      sortable: true,
      valueTransformer: renderScore,
    });

    result.push({
      Header: intl.formatMessage({ id: 'time' }),
      value: intl.formatMessage({ id: 'time' }),
      accessor: 'time',
      Footer: formatValue(results.score, results.maxScore),
      sortable: true,
      valueTransformer: (time) => {
        return <AssignmentResultTimeCell time={time} timeLimit={assignment.timeLimit} />;
      },
    });

    exercises?.forEach((e) => {
      const subColumns = e.questions.map((q) => ({
        Header: (
          <AssignmentResultQuestionHeadCell
            question={q}
            onOpenPopover={handleOpenQuestionPopover}
            onClosePopover={handleCloseQuestionPopover}
            href={`/${courseId}/subjects/${e.subject}/exercise-view/${e.type || DEFAULT_EXERCISE_TYPE}/${e._id}?from=${courseId}/assignments/${
              assignment.id
            }/details/results&title=${assignment.name}`}
          />
        ),
        value: `${e.code} - ${q.name}`,
        Footer: formatValue(q.answerTypes ? q.answerTypes.positive : 0, q.answers),
        valueTransformer: renderScore,
        accessor: q._id,
      }));

      result.push({
        Header: e.code,
        columns: subColumns,
      });
    });

    return result;
  }, [intl, exercises, renderScore, results, assignment, courseId, classes, handleCloseQuestionPopover, handleOpenQuestionPopover, students]);

  const extraFilters = useMemo(
    () => [
      {
        component: (
          <FormControlLabel
            key="students"
            control={<Checkbox checked={onlyListStudentsWithoutAnswers} onChange={onToggleOnlyListStudentsWithoutAnswers} />}
            label={intl.formatMessage({
              id: 'assignmentResultsTable.filter.onlyStudentsWithoutAnswers',
            })}
          />
        ),
      },
    ],
    [intl, onlyListStudentsWithoutAnswers, onToggleOnlyListStudentsWithoutAnswers]
  );

  return (
    <>
      <TSTable
        title={intl.formatMessage({ id: 'assignmentResults' })}
        data={students}
        columns={columns}
        onSearch={onFilterResults}
        extraFilters={extraFilters}
        loading={loading}
        xlsxFileName={results.assignmentName}
        allowDownloadAsImage
        allowDownloadAsXLSX
        getDataToExport={onGetXlsxData}
      />
      <QuestionStatsPopover {...questionPopoverData} onClose={handleCloseQuestionPopover} />
    </>
  );
};

AssignmentResultsTable.propTypes = {
  courseId: PropTypes.string,
  results: PropTypes.object,
  students: PropTypes.array,
  loading: PropTypes.bool,
  onlyListStudentsWithoutAnswers: PropTypes.bool,
  assignment: PropTypes.object,

  onFilterResults: PropTypes.func,
  onToggleOnlyListStudentsWithoutAnswers: PropTypes.func,
  onGetXlsxData: PropTypes.func,
};

AssignmentResultsTable.defaultProps = {
  courseId: undefined,
  assignment: {},
  students: [],
  results: {},
  loading: false,
  onlyListStudentsWithoutAnswers: false,

  onFilterResults() {},
  onToggleOnlyListStudentsWithoutAnswers() {},
  onGetXlsxData: undefined,
};

export default AssignmentResultsTable;
